'use client';

import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import type { PostLoginAction } from './postLoginActions';
import { PostLoginActionType, postLoginActionsStorage } from './postLoginActions';
import { ChangeEmailModalDynamic } from './ChangeEmailModal.dynamic';
import { ChangeBillingAddressModalDynamic } from './ChangeBillingAddressModal.dynamic';
import { TACReconfirmationModalDynamic } from './TACReconfirmationModal.dynamic';

/**
 * PostLoginActionsHandler monitors localStorage for pending post-login actions
 * and displays them sequentially (one at a time) to the user.
 *
 * Actions are removed from the queue when:
 * - User successfully completes the action
 * - User dismisses/closes the modal
 * - User logs out
 */
export const PostLoginActionsHandler: FC = () => {
  const [currentAction, setCurrentAction] = useState<PostLoginAction | null>(null);
  const { loggedIn } = useLoginState();

  // Monitor localStorage for action changes
  useEffect(() => {
    const checkActions = () => {
      const actions = postLoginActionsStorage.getActions();
      if (actions.length > 0 && !currentAction) {
        setCurrentAction(actions[0]);
      } else if (actions.length === 0 && currentAction) {
        setCurrentAction(null);
      }
    };

    // Check on mount
    checkActions();

    // Listen for custom events (same-tab changes)
    window.addEventListener('postLoginActionsChanged', checkActions);

    // Listen for storage events (cross-tab changes)
    window.addEventListener('storage', checkActions);

    return () => {
      window.removeEventListener('postLoginActionsChanged', checkActions);
      window.removeEventListener('storage', checkActions);
    };
  }, [currentAction]);

  // Clear actions when user logs out
  useEffect(() => {
    if (!loggedIn && postLoginActionsStorage.getActions().length > 0) {
      postLoginActionsStorage.clearActions();
      setCurrentAction(null);
    }
  }, [loggedIn]);

  // Handler for completing/dismissing an action
  const handleComplete = () => {
    postLoginActionsStorage.removeFirstAction();
    setCurrentAction(null);
  };

  if (!currentAction) return null;

  // Render appropriate modal based on action type
  switch (currentAction.type) {
    case PostLoginActionType.CHECK_EMAIL:
      return <ChangeEmailModalDynamic action={currentAction} onClose={handleComplete} />;
    case PostLoginActionType.CHECK_BILLING_ADDRESS:
      return <ChangeBillingAddressModalDynamic action={currentAction} onClose={handleComplete} />;
    case PostLoginActionType.TAC_RECONFIRMATION:
      return <TACReconfirmationModalDynamic action={currentAction} onClose={handleComplete} />;
    default:
      // Unknown action type, remove it
      handleComplete();
      return null;
  }
};
